"use client";
import React, { useState } from "react";
import styles from "./ExpertGuideSection.module.scss";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { toKebabCase } from "@utils/stringFormatters";
import ImageResponsive from "@components/atoms/ImageResponsive";
import VideoModal from "@components/molecules/VideoModal/VideoModal";
import { DeviceType, VideoMetadata, VideoSource } from "@interfaces/video";
import { createVideoSource } from "@utils/videoUtils";
import { getLoginUrl } from "@utils/getLoginUrl";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";

interface ExpertGuideSectionClientWrapperProps {
  mmName: string;
  exclusiveMedia: any;
  isLoggedIn: boolean;
  makeName: string;
  modelName: string;
  variantName?: string;
  cityName?: string;
  pageType?: string;
  carAvailability?: string;
}

export default function ExpertGuideSectionClientWrapper({
  mmName,
  exclusiveMedia,
  isLoggedIn,
  makeName,
  modelName,
  variantName,
  cityName,
  pageType,
  carAvailability,
}: ExpertGuideSectionClientWrapperProps) {
  const [selectedVideo, setSelectedVideo] = useState<VideoSource | null>(null);
  const [selectedMetadata, setSelectedMetadata] =
    useState<VideoMetadata | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.DESKTOP);

  const handleVideoClick = (url: string, metadata?: VideoMetadata) => {
    setSelectedVideo(createVideoSource(url));
    setSelectedMetadata(metadata ?? null);
    setIsModalOpen(true);
  };

  const handleModalClose = async () => {
    // Track expert video close event
    if (selectedMetadata) {
      await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_close_expert_video",
          video_tag: selectedMetadata?.title || "",
          closing_time: new Date().toISOString(),
          car_availability: carAvailability,
          city_name: cityName,
          vehicle_make: makeName,
          vehicle_model: modelName,
          ...(variantName && { vehicle_variant: variantName }),
        },
      });
    }

    setIsModalOpen(false);
    setSelectedVideo(null);
    setSelectedMetadata(null);
  };

  const handlePlayButtonClick = async () => {
    // Track expert video play event
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_expert_video",
        video_locked: exclusiveMedia?.locked ? "Yes" : "No",
        video_tag: exclusiveMedia?.metadata?.title || "",
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: makeName,
        vehicle_model: modelName,
        ...(variantName && { vehicle_variant: variantName }),
      },
    });

    if (!isLoggedIn) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("loginType", "Expert video");
        localStorage.setItem(
          "MMV",
          JSON.stringify({
            make: makeName || "",
            model: modelName || "",
            variant: variantName || "",
            color: "",
          })
        );
      }
      window.location.href = getLoginUrl(window.location.href);
      return;
    }
    handleVideoClick(exclusiveMedia?.url ?? "", exclusiveMedia?.metadata);
  };

  return (
    <>
      <div className={styles["video-guide"]}>
        {/* Video thumbnail and play button */}
        <div className={styles["video-thumbnail-container"]}>
          {/* Replace with actual image component when available */}
          <ImageResponsive
            filePath={`${toKebabCase(makeName)}/${toKebabCase(modelName)}`}
            imageTag={`Hero-Transparent`}
            width={500}
            height={500}
            alt="Video Thumbnail"
            className={styles.videoThumbnail}
            loading="lazy"
          />
          <div
            className={styles["play-button"]}
            onClick={handlePlayButtonClick}
          >
            <Image
              src={`${assetLoader()}/icons/play_circle.svg`}
              alt="Play"
              loading="lazy"
              width={40}
              height={40}
              className={styles.play}
            />
          </div>
          <div className={styles.exclusiveBadge}>
            <Image
              src={`${assetLoader()}/icons/icon-exclusive.svg`}
              alt="Exclusive"
              width={12}
              height={12}
              loading="lazy"
            />
            Exclusive
          </div>
        </div>
        <div className={styles["video-details"]}>
          <h3 className={styles["video-title"]}>
            {`Which ${mmName} variant should you choose - Expert opinion`}
          </h3>
          <div className={styles["expert-info"]}>
            {/* Replace with actual expert image when available */}
            <Image
              src={`${assetLoader()}/images/S_V_P.jpg`}
              alt="Expert"
              width={40}
              height={40}
              className={styles["expert-image"]}
              loading="lazy"
            />
            <div>
              <p className={styles["expert-name"]}>Siddharth Patankar</p>
              <p className={styles["expert-title"]}>Executive VP</p>
            </div>
          </div>
        </div>
      </div>

      <VideoModal
        video={selectedVideo}
        metadata={selectedMetadata}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        deviceType={deviceType}
        config={{
          autoplay: true,
          muted: false,
          showTitle: true,
          showProgress: true,
          showVolume: true,
          showClose: true,
        }}
        events={{
          onPlay: () => console.log("Video started playing"),
          onPause: () => console.log("Video paused"),
          onEnded: () => console.log("Video ended"),
          onError: (error) => console.error("Video error:", error),
        }}
      />
    </>
  );
}
